import { Stack, Typography } from '@mui/material';
import {
  ConductConnotation,
  ConductSelectTypesFilterValue,
  ConductType,
  FilterKeys,
  useGetConductTypesForSchoolQuery,
} from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import { flattenConductTypeOptions } from '../utils';
import { ConductTypeOptionTagSelect } from './ConductTypeOptionTagSelect';
import { ConductTypeOptionTagSelectProps } from './ConductTypeOptionTagSelect';
import { ConductTypeSelectRow } from './ConductTypeSelectRow';
import { ConductTypeTagSelect, ConductTypeTagSelectProps } from './ConductTypeTagSelect';
import {
  applySelectedConductTypeId,
  applySelectedConductTypeOption,
  onSelectConductTypeOrConductTypeOption,
} from './selectorHelpers';

export const filterConductTypes = (ct: ConductType, query: string) => {
  if (!query) return ct;

  return (
    ct.name.toLowerCase().includes(query.toLowerCase()) ||
    ct.select_type_options?.some((option) =>
      option.label.toLowerCase().includes(query.toLowerCase()),
    )
  );
};

type ConductTypeExpandedSelectProps = PropsWithChildren<{
  schoolId: string;
  selectedConductTypeIds: string[];
  selectedConductTypeOptions: ConductSelectTypesFilterValue[];
  onApply: (ids: string[], options: ConductSelectTypesFilterValue[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;

export const ConductTypeExpandedSelect: FC<ConductTypeExpandedSelectProps> = ({
  schoolId,
  selectedConductTypeIds: initialSelectedConductTypeIds,
  selectedConductTypeOptions: initialSelectedConductTypeOptions,
  onApply,
  onClose,
  onClear,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState({
    [FilterKeys.ConductType]: initialSelectedConductTypeIds,
    [FilterKeys.ConductSelectTypes]: initialSelectedConductTypeOptions,
  });

  const { data } = useGetConductTypesForSchoolQuery(schoolId, {
    refetchOnMount: 'always',
  });

  const onSelectConductTypeId = useCallback((id: string) => {
    setSelected(applySelectedConductTypeId(id));
  }, []);

  const onSelectConductTypeOption = useCallback(
    (conductTypeId: string, label: string) =>
      setSelected(applySelectedConductTypeOption(conductTypeId, label)),
    [],
  );

  const handleSelectConductTypeOption = useCallback(
    (conductTypeId: string, label: string) => {
      onSelectConductTypeOrConductTypeOption({
        conductTypes: data,
        selectedConductTypeOptions: selected[FilterKeys.ConductSelectTypes],
        onSelectConductTypeId,
        onSelectConductTypeOption,
      })(conductTypeId, label);
    },
    [data, onSelectConductTypeId, onSelectConductTypeOption, selected],
  );

  const handleApply = useCallback(() => {
    onApply(selected[FilterKeys.ConductType], selected[FilterKeys.ConductSelectTypes]);
    onClose();
  }, [selected, onApply, onClose]);

  const renderContent = useCallback(() => {
    if (!data) return <SelectContentSkeleton />;

    const positive = data
      .filter((ct) => ct.connotation === ConductConnotation.POSITIVE)
      .filter((ct) => filterConductTypes(ct, query));
    const negative = data
      .filter((ct) => ct.connotation === ConductConnotation.NEGATIVE)
      .filter((ct) => filterConductTypes(ct, query));

    if (!positive.length && !negative.length)
      return (
        <Typography p={1}>
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );

    return (
      <Stack gap={2} m={1}>
        {!!positive.length && (
          <Stack gap={1}>
            <Typography variant="h4">
              <FormattedMessage id="conduct-Type-positive" />
            </Typography>

            {positive.map((conductType) => (
              <ConductTypeSelectRow
                key={conductType.id}
                isSelected={selected[FilterKeys.ConductType].includes(conductType.id)}
                onSelectConductType={onSelectConductTypeId}
                onSelectConductTypeOption={handleSelectConductTypeOption}
                conductType={conductType}
                selectedConductTypeOptions={
                  selected[FilterKeys.ConductSelectTypes].find(
                    (ct) => ct.conduct_type_id === conductType.id,
                  )?.conduct_type_options ?? []
                }
              />
            ))}
          </Stack>
        )}
        {!!negative.length && (
          <Stack gap={1}>
            <Typography variant="h4">
              <FormattedMessage id="conduct-Type-negative" />
            </Typography>

            {negative.map((conductType) => (
              <ConductTypeSelectRow
                key={conductType.id}
                isSelected={selected[FilterKeys.ConductType].includes(conductType.id)}
                onSelectConductType={onSelectConductTypeId}
                onSelectConductTypeOption={handleSelectConductTypeOption}
                conductType={conductType}
                selectedConductTypeOptions={
                  selected[FilterKeys.ConductSelectTypes].find(
                    (ct) => ct.conduct_type_id === conductType.id,
                  )?.conduct_type_options ?? []
                }
              />
            ))}
          </Stack>
        )}
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [
    data,
    handleApply,
    handleSelectConductTypeOption,
    onClear,
    onSelectConductTypeId,
    query,
    selected,
  ]);

  return (
    <ExpandedSelect
      hasSelectedValue={
        selected[FilterKeys.ConductType].length > 0 ||
        selected[FilterKeys.ConductSelectTypes].length > 0
      }
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
    >
      {renderConductTypeTags({
        ids: selected[FilterKeys.ConductType],
        tagProps: { schoolId, size: 'small', sx: { maxWidth: 200 } },
        onDelete: onSelectConductTypeId,
      })}
      {renderConductTypeOptionTags({
        options: selected[FilterKeys.ConductSelectTypes],
        onDelete: handleSelectConductTypeOption,
        tagProps: { schoolId, size: 'small', sx: { maxWidth: 200 } },
      })}

      <SelectSearchInput ref={inputRef} autoFocus value={query} onChangeText={setQuery} />
    </ExpandedSelect>
  );
};

type RenderConductTypeTagsParams = {
  ids: string[];
  onDelete?: (v: string) => void;
  onClick?: (v: string) => void;
  tagProps:
    | Omit<ConductTypeTagSelectProps, 'id'>
    | ((v: string) => Omit<ConductTypeTagSelectProps, 'id'>);
};

export const renderConductTypeTags = ({
  ids,
  onDelete,
  onClick,
  tagProps,
}: RenderConductTypeTagsParams) => {
  return ids.map((v) => (
    <ConductTypeTagSelect
      key={v}
      id={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
};

type RenderConductTypeOptionTagsParams = {
  options: ConductSelectTypesFilterValue[];
  onDelete?: (conductTypeId: string, label: string) => void;
  onClick?: (conductTypeId: string, label: string) => void;
  tagProps:
    | Omit<ConductTypeOptionTagSelectProps, 'conductTypeId' | 'label'>
    | ((v: string) => Omit<ConductTypeOptionTagSelectProps, 'conductTypeId' | 'label'>);
};

export const renderConductTypeOptionTags = ({
  options,
  onDelete,
  onClick,
  tagProps,
}: RenderConductTypeOptionTagsParams) => {
  const optionsToRender = flattenConductTypeOptions(options);

  return optionsToRender.map(({ conductTypeId, label }) => (
    <ConductTypeOptionTagSelect
      key={`${conductTypeId}-${label}`}
      id={`${conductTypeId}-${label}`}
      label={label}
      conductTypeId={conductTypeId}
      onClick={onClick ? () => onClick(conductTypeId, label) : undefined}
      onDelete={onDelete ? () => onDelete(conductTypeId, label) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(label) : tagProps)}
    />
  ));
};
